import { useState } from 'react'
import { Plus, Send, Ticket } from 'lucide-react'
import { foutTekst, vraag } from '../lib/api'
import { datum, getal } from '../lib/opmaak'
import { Lade } from '../onderdelen/Lade'
import { AfbeeldingKiezen } from './AfbeeldingKiezen'
import { Laden, useLaad, type Sessie } from './Beheer'

/*
 * Promoties en vouchers. Een voucher is gratis te gebruiken (geen punten),
 * één keer, met een medewerker-PIN op de tablet. Uitdelen aan een groep, aan
 * één klant (Klanten), of automatisch bij registratie of op de verjaardag.
 */

interface Promotie {
  id: string; naam: string; omschrijving: string; actief: boolean; alle: boolean; vestigingen: string[]
  totEnMet: string | null; dagenGeldig: number | null; automatisch: 'geen' | 'registratie' | 'verjaardag'
  afbeelding: string | null; uitgegeven: number; gebruikt: number
}
interface Data { promoties: Promotie[]; uitgedeeld?: number; bereikt?: number }

const AUTOMATISCH = { geen: 'Handmatig uitdelen', registratie: 'Automatisch bij registratie', verjaardag: 'Automatisch op de verjaardag' }

export function Vouchers({ s }: { s: Sessie }) {
  const { data, fout, laad, zet } = useLaad<Data>('promoties')
  const [bewerk, zetBewerk] = useState<Partial<Promotie> | null>(null)
  const [delen, zetDelen] = useState<Promotie | null>(null)
  if (!data) return <><div className="pagina-kop"><h2>Vouchers</h2></div><Laden fout={fout} opnieuw={laad} /></>

  return (
    <>
      <div className="pagina-kop">
        <h2>Vouchers</h2>
        <button className="knop geel" onClick={() => zetBewerk({ actief: true, alle: true, vestigingen: [], automatisch: 'geen' })}><Plus size={19} />Nieuwe voucher</button>
      </div>
      <p className="stil" style={{ marginBottom: 14 }}>Een voucher kost geen punten en is één keer te gebruiken: de klant scant zijn kaart en kiest de voucher, een medewerker bevestigt met zijn PIN.</p>
      {data.promoties.length === 0 ? <div className="leeg-staat"><Ticket size={28} />Nog geen promoties.</div> : (
        <div className="kaart" style={{ padding: '4px 0' }}>
          <table className="tabel kaartjes">
            <thead><tr><th>Voucher</th><th>Hoe</th><th className="getal">Uitgegeven</th><th className="getal">Gebruikt</th><th>Status</th><th /></tr></thead>
            <tbody>
              {data.promoties.map((p) => (
                <tr key={p.id} className="klikbaar" onClick={() => zetBewerk(p)}>
                  <td className="hoofd">
                    <span className="rij" style={{ flexWrap: 'nowrap' }}>
                      {p.afbeelding ? <img className="beloning-foto klein" src={p.afbeelding} alt="" /> : <Ticket size={18} className="stil" />}
                      <span><b>{p.naam}</b>{p.omschrijving && <><br /><span className="stil klein">{p.omschrijving}</span></>}</span>
                    </span>
                  </td>
                  <td data-l="Hoe">{AUTOMATISCH[p.automatisch]}{p.totEnMet ? ` · t/m ${datum(p.totEnMet)}` : ''}</td>
                  <td className="getal" data-l="Uitgegeven">{getal(p.uitgegeven)}</td>
                  <td className="getal" data-l="Gebruikt">{getal(p.gebruikt)}</td>
                  <td data-l="Status">{p.actief ? <span className="badge open">Actief</span> : <span className="badge">Uit</span>}</td>
                  <td data-l="">
                    <button className="knop klein-knop" disabled={!p.actief} onClick={(e) => { e.stopPropagation(); zetDelen(p) }}><Send size={15} />Uitdelen</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Lade titel={bewerk?.id ? bewerk.naam ?? '' : 'Nieuwe voucher'} open={!!bewerk} sluit={() => zetBewerk(null)} breed
        kinderen={bewerk && <PromotieFormulier p={bewerk} s={s} klaar={(r) => { zet(r); zetBewerk(null) }}
          bijgewerkt={(r: Data) => { zet(r); zetBewerk(r.promoties.find((x) => x.id === bewerk.id) ?? bewerk) }} />} />
      <Lade titel={`Uitdelen: ${delen?.naam ?? ''}`} open={!!delen} sluit={() => zetDelen(null)}
        kinderen={delen && <Uitdelen p={delen} s={s} klaar={(r) => zet(r)} />} />
    </>
  )
}

function PromotieFormulier({ p, s, klaar, bijgewerkt }: { p: Partial<Promotie>; s: Sessie; klaar: (r: Data) => void; bijgewerkt: (r: Data) => void }) {
  const [f, zet] = useState({
    naam: p.naam ?? '', omschrijving: p.omschrijving ?? '', actief: p.actief !== false, totEnMet: p.totEnMet ?? '',
    dagenGeldig: p.dagenGeldig ? String(p.dagenGeldig) : '', automatisch: p.automatisch ?? 'geen',
  })
  const [alle, zetAlle] = useState(p.alle !== false)
  const [vest, zetVest] = useState<string[]>(p.vestigingen ?? [])
  const [fout, zetFout] = useState<string | null>(null)
  return (
    <form className="formulier" onSubmit={async (e) => {
      e.preventDefault(); zetFout(null)
      try {
        klaar(await vraag('promotie-opslaan', { id: p.id, ...f, totEnMet: f.totEnMet || null, dagenGeldig: f.dagenGeldig || null, alle, vestigingen: alle ? [] : vest }))
      } catch (x) { zetFout(foutTekst(x)) }
    }}>
      <div className="twee-kol">
        <label className="veld" style={{ gridColumn: '1 / -1' }}><span>Naam</span><input value={f.naam} onChange={(e) => zet({ ...f, naam: e.target.value })} placeholder="Gratis wassing Elsloo" required /></label>
        <label className="veld" style={{ gridColumn: '1 / -1' }}><span>Omschrijving</span><textarea rows={2} style={{ resize: 'vertical' }} value={f.omschrijving} onChange={(e) => zet({ ...f, omschrijving: e.target.value })} placeholder="Kom kennismaken met onze nieuwe vestiging" /></label>
        <label className="veld"><span>Geldig tot en met</span><input type="date" value={f.totEnMet} onChange={(e) => zet({ ...f, totEnMet: e.target.value })} /><small>Laat leeg als hij niet verloopt.</small></label>
        <label className="veld"><span>Geldig na uitgeven (dagen)</span><input value={f.dagenGeldig} inputMode="numeric" onChange={(e) => zet({ ...f, dagenGeldig: e.target.value.replace(/\D/g, '') })} placeholder="bijv. 30" /><small>Laat leeg voor onbeperkt.</small></label>
      </div>
      <label className="veld"><span>Uitdelen</span>
        <select value={f.automatisch} onChange={(e) => zet({ ...f, automatisch: e.target.value as Promotie['automatisch'] })}>
          {Object.entries(AUTOMATISCH).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <small>{f.automatisch === 'verjaardag' ? 'Elk jaar opnieuw op de verjaardag, aan wie zijn verjaardag heeft ingevuld.'
          : f.automatisch === 'registratie' ? 'Iedereen die een nieuwe kaart registreert, krijgt hem meteen.'
            : 'Na het opslaan deel je hem uit met de knop Uitdelen, of per klant bij Klanten.'}</small>
      </label>
      <div className="veld">
        <span>Geldt op</span>
        <div className="chips">
          <button type="button" className="chip" aria-pressed={alle} onClick={() => zetAlle(true)}>Alle vestigingen</button>
          <button type="button" className="chip" aria-pressed={!alle} onClick={() => zetAlle(false)}>Kies vestigingen</button>
        </div>
        {!alle && (
          <div className="keuzes">
            {s.vestigingen.filter((v) => v.actief || vest.includes(v.id)).map((v) => (
              <label key={v.id} className="keuze">
                <input type="checkbox" checked={vest.includes(v.id)} onChange={(e) => zetVest(e.target.checked ? [...vest, v.id] : vest.filter((x) => x !== v.id))} />
                {v.naam.replace(/^Truckwash\s*/i, '')}
              </label>
            ))}
          </div>
        )}
      </div>
      <div className="veld"><span>Afbeelding</span>
        {p.id ? <AfbeeldingKiezen actie="promotie-afbeelding" id={p.id} url={p.afbeelding ?? null} bijgewerkt={bijgewerkt} /> : <small>Sla eerst op; daarna kun je een afbeelding toevoegen.</small>}
      </div>
      <label className="vink"><input type="checkbox" checked={f.actief} onChange={(e) => zet({ ...f, actief: e.target.checked })} /><span>Actief. Uitgezet is hij niet meer uit te delen of te gebruiken.</span></label>
      {fout && <div className="melding fout">{fout}</div>}
      <div className="knoppenrij"><button className="knop geel">Opslaan</button></div>
    </form>
  )
}

function Uitdelen({ p, s, klaar }: { p: Promotie; s: Sessie; klaar: (r: Data) => void }) {
  const [doel, zetDoel] = useState('actief')
  const [vestiging, zetVestiging] = useState(s.vestigingen[0]?.id ?? '')
  const [bezig, zetBezig] = useState(false)
  const [uit, zetUit] = useState<string | null>(null)
  return (
    <div className="formulier">
      <label className="veld"><span>Aan wie</span>
        <select value={doel} onChange={(e) => zetDoel(e.target.value)}>
          <option value="actief">Gewassen in de laatste 12 maanden</option>
          <option value="iedereen">Alle klanten</option>
          <option value="toestemming">Met toestemming voor mail</option>
          <option value="vestiging">Ooit op één vestiging geweest</option>
        </select>
      </label>
      {doel === 'vestiging' && (
        <label className="veld"><span>Vestiging</span>
          <select value={vestiging} onChange={(e) => zetVestiging(e.target.value)}>
            {s.vestigingen.map((v) => <option key={v.id} value={v.id}>{v.naam.replace(/^Truckwash\s*/i, '')}</option>)}
          </select>
        </label>
      )}
      <p className="stil klein">Wie deze voucher al heeft, krijgt er geen tweede. Een klant ziet hem meteen in zijn account en op de tablet.</p>
      {uit && <div className="melding ok">{uit}</div>}
      <button className="knop geel breed" disabled={bezig} onClick={async () => {
        zetBezig(true); zetUit(null)
        try {
          const r = await vraag<Data>('promotie-uitdelen', { id: p.id, doelgroep: doel, vestiging })
          zetUit(`${getal(r.uitgedeeld ?? 0)} vouchers uitgedeeld (${getal(r.bereikt ?? 0)} klanten in de groep).`)
          klaar(r)
        } catch (e) { zetUit(foutTekst(e)) } finally { zetBezig(false) }
      }}><Send size={18} />{bezig ? 'Bezig…' : 'Uitdelen'}</button>
    </div>
  )
}
